import {getTestProps} from '@benchling/js-test-utils';
import {Box, createStyles, Divider, Grid, Text, Title} from '@mantine/core';
import classNames from 'classnames';
import React, {KeyboardEvent} from 'react';

import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {CountsOfCreatedComponents} from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportUtil';
import {TranslationParameters} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import {
  AntibodyComponentAndRelatedSchemas,
  NdcRelatedSchemasConfig,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import {OBJECTS_CREATABLE_PERMISSION, OBJECTS_REGISTERABLE_PERMISSION} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {CodonTranslationTable} from 'biojs/BioConstants';
import GeneticCodeDropdown from 'molbio/translations/GeneticCodeDropdown';
import EntrySearchInput from 'notebook/entries/EntrySearchInput';
import ProjectFolderField from 'registry/ProjectFolderField';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import {SearchContext} from 'search/SearchConstants';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LABEL_STYLES} from 'ui-library/inputs/InputConstants';
import {GenericEvent} from 'util/Types';

const useStyles = createStyles((theme) => ({
  summaryList: {
    paddingInlineStart: theme.spacing.lg,
  },
  schemaColumn: {
    width: '300px',
    maxWidth: '300px',
  },
  fieldLabelReducedMargin: {
    marginBottom: 0,
  },
}));

export interface FinalizePaneProps {
  projectFolderId: string | null;
  setProjectFolder: (projectFolderId: string) => void;
  relatedSchemasConfig: NdcRelatedSchemasConfig;
  setRelatedSchemasConfig: (relatedSchemasConfig: NdcRelatedSchemasConfig) => void;
  translationParameters: TranslationParameters;
  setTranslationParameters: (translationParameters: TranslationParameters) => void;
  countsOfCreatedComponents: CountsOfCreatedComponents;
  selectedAntibodyFormat: AntibodyFormat;
  selectedSchemaName: string;
  componentAndRelatedSchemas: AntibodyComponentAndRelatedSchemas | null;
  componentAndRelatedSchemasErrorMessage: string | null;
  selectedDocumentId: string | null;
  setSelectedDocumentId: (documentId: string | null) => void;
  renderDnaSchemaSelectors: boolean;
}

interface SchemaTableRowBase {
  icon: FolderItemIcon;
  componentTypeLabel: string;
  count: number;
}

/** A row in the schema table. This is a union of rows for user-selectable schemas (related schemas) and rows
 * for non-user-selectable enforced data model schemas (component schemas). */
type SchemaTableRow = SchemaTableRowBase &
  (
    | {
        isEnforced: false;
        relatedSchemaConfigField: keyof NdcRelatedSchemasConfig;
        /** The IDs of the schemas the user is allowed to select from the dropdown. Null if
         * componentAndRelatedSchemas is null. */
        allowedSchemaIds: Set<string> | null;
      }
    | {
        isEnforced: true;
        /** Schema names determined by the data model to be shown instead of a dropdown. Null if
         * componentAndRelatedSchemas is null. */
        enforcedSchemaNames: Set<string> | null;
      }
  );

function blurOnEnter(e: KeyboardEvent<HTMLElement>): void {
  if (e.key === 'Enter') {
    e.preventDefault();
    e.currentTarget.blur();
  }
}

export default function FinalizePane({
  projectFolderId,
  setProjectFolder,
  relatedSchemasConfig,
  setRelatedSchemasConfig,
  translationParameters,
  setTranslationParameters,
  countsOfCreatedComponents,
  selectedAntibodyFormat,
  selectedSchemaName,
  componentAndRelatedSchemas,
  componentAndRelatedSchemasErrorMessage,
  selectedDocumentId,
  setSelectedDocumentId,
  renderDnaSchemaSelectors,
}: FinalizePaneProps): JSX.Element {
  const {classes} = useStyles();

  const tableRows: Array<SchemaTableRow> = [
    ...(renderDnaSchemaSelectors
      ? [
          {
            isEnforced: false as const,
            icon: FolderItemIcon.Sequence,
            componentTypeLabel: 'DNA domain',
            count: countsOfCreatedComponents.dnaDomains,
            relatedSchemaConfigField: 'dnaDomainSchemaId' as const,
            allowedSchemaIds: componentAndRelatedSchemas?.dnaDomainSchemaIds ?? null,
          },
        ]
      : []),
    {
      isEnforced: true,
      icon: FolderItemIcon.Protein,
      componentTypeLabel: 'AA domain',
      count: countsOfCreatedComponents.aaDomains,
      enforcedSchemaNames:
        componentAndRelatedSchemas != null
          ? new Set(componentAndRelatedSchemas.aaDomainSchemas.map((s) => s.name))
          : null,
    },
    ...(renderDnaSchemaSelectors
      ? [
          {
            isEnforced: false as const,
            icon: FolderItemIcon.BasicFolderItem,
            componentTypeLabel: 'DNA variable pair',
            count: countsOfCreatedComponents.dnaPairs,
            relatedSchemaConfigField: 'dnaPairSchemaId' as const,
            allowedSchemaIds: componentAndRelatedSchemas?.dnaPairSchemaIds ?? null,
          },
        ]
      : []),
    {
      isEnforced: false,
      icon: FolderItemIcon.BasicFolderItem,
      componentTypeLabel: 'AA variable pair',
      count: countsOfCreatedComponents.aaPairs,
      relatedSchemaConfigField: 'aaPairSchemaId',
      allowedSchemaIds: componentAndRelatedSchemas?.aaPairSchemaIds ?? null,
    },
    {
      isEnforced: true,
      icon: FolderItemIcon.Protein,
      componentTypeLabel: 'AA chain',
      count: countsOfCreatedComponents.aaChains,
      enforcedSchemaNames:
        componentAndRelatedSchemas != null
          ? new Set(componentAndRelatedSchemas.aaChainSchemas.map((s) => s.name))
          : null,
    },
    ...(renderDnaSchemaSelectors
      ? [
          {
            isEnforced: false as const,
            icon: FolderItemIcon.Sequence,
            componentTypeLabel: 'DNA chain',
            count: countsOfCreatedComponents.dnaChains,
            relatedSchemaConfigField: 'dnaChainSchemaId' as const,
            allowedSchemaIds: componentAndRelatedSchemas?.dnaChainSchemaIds ?? null,
          },
        ]
      : []),
    {
      isEnforced: true,
      icon: FolderItemIcon.Antibody,
      componentTypeLabel: selectedAntibodyFormat.name,
      count: countsOfCreatedComponents.antibodies,
      enforcedSchemaNames: new Set([selectedSchemaName]),
    },
  ];

  const pairCount = countsOfCreatedComponents.dnaPairs + countsOfCreatedComponents.aaPairs;
  const dnaSequenceCount = countsOfCreatedComponents.dnaDomains + countsOfCreatedComponents.dnaChains;
  const aaSequenceCount = countsOfCreatedComponents.aaDomains + countsOfCreatedComponents.aaChains;

  return (
    <div>
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        hideIcon={true}
        text={`Estimated entities to be created: ${countsOfCreatedComponents.totalEntities.toLocaleString('en-US')}`}
        supportingInfo={
          <Box mt="xs">
            <Text size="sm">Here is a breakdown of all the new entities that will be created:</Text>
            <ul className={classes.summaryList}>
              <li>
                <Text size="sm" component="span">
                  {countsOfCreatedComponents.antibodies}{' '}
                  {pluralize(countsOfCreatedComponents.antibodies, 'protein')}
                </Text>
              </li>
              {pairCount > 0 && (
                <li>
                  <Text size="sm" component="span">
                    {pairCount} variable pair custom {pluralize(pairCount, 'entity')}
                  </Text>
                </li>
              )}
              {dnaSequenceCount > 0 && (
                <li>
                  <Text size="sm" component="span">
                    {dnaSequenceCount} DNA {pluralize(dnaSequenceCount, 'sequence')}
                  </Text>
                </li>
              )}
              {aaSequenceCount > 0 && (
                <li>
                  <Text size="sm" component="span">
                    {aaSequenceCount} AA {pluralize(aaSequenceCount, 'sequence')}
                  </Text>
                </li>
              )}
            </ul>
            <Text size="sm" mt="xs">
              This count is based on per-row values and does not account for duplicates in your spreadsheet.
              Repeated values across rows will only be registered once.
            </Text>
          </Box>
        }
        action={{
          actionType: InlineBannerActionType.ACCORDION,
          accordionLabels: {moreLabel: 'See More', lessLabel: 'See Less'},
          rightAlign: true,
          initialShowMore: true,
        }}
      />

      <Title order={5} fw={600} mt="xl">
        Protein component schemas
      </Title>
      <Text size="sm" mb="xl">
        Select schemas for protein components you are about to create
      </Text>

      {componentAndRelatedSchemasErrorMessage != null && (
        <InlineBanner bannerType={InlineBannerType.ERROR} text={componentAndRelatedSchemasErrorMessage} />
      )}

      <table className="table" {...getTestProps({component: 'FinalizePane', element: 'schemaTable'})}>
        <thead>
          <tr>
            <th colSpan={2}>Component type</th>
            <th># new entities</th>
            <th>Schema</th>
          </tr>
        </thead>
        <tbody>
          {tableRows.map((row) => (
            <tr key={row.componentTypeLabel}>
              <td style={{width: 44, textAlign: 'center', verticalAlign: 'middle'}}>
                <BenchlingIcon icon={row.icon} style={{fontSize: 16, display: 'block'}} />
              </td>
              <td className="bulk-import--column-text-column">
                <span className="bulk-import--table-text">{row.componentTypeLabel}</span>
              </td>
              <td className="bulk-import--column-text-column">
                <span className="bulk-import--table-text">{row.count}</span>
              </td>
              <td
                className={classNames(classes.schemaColumn, {
                  'bulk-import--column-text-column': row.isEnforced,
                })}
              >
                {componentAndRelatedSchemasErrorMessage != null ? (
                  <Text size="sm" color="dimmed">
                    —
                  </Text>
                ) : componentAndRelatedSchemas == null ? (
                  <div>
                    <HorizontalThrobber />
                  </div>
                ) : row.isEnforced ? (
                  <span className="bulk-import--table-text">{[...row.enforcedSchemaNames!].join(', ')}</span>
                ) : (
                  <TagSchemaSelector
                    selectedTagSchemaId={relatedSchemasConfig[row.relatedSchemaConfigField]}
                    onChange={(tagSchema) => {
                      setRelatedSchemasConfig({
                        ...relatedSchemasConfig,
                        [row.relatedSchemaConfigField]: tagSchema?.id ?? null,
                      });
                    }}
                    shouldDefaultIfSingleItem={true}
                    requiredPermissions={[OBJECTS_CREATABLE_PERMISSION, OBJECTS_REGISTERABLE_PERMISSION]}
                    tagSchemaFilterFn={(tagSchema) => row.allowedSchemaIds!.has(tagSchema.id)}
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Divider my="lg" />

      <Grid mt="md">
        <Grid.Col span={6} {...getTestProps({component: 'FinalizePane', element: 'geneticCodeDropdown'})}>
          <FormGroup>
            <FieldLabel label="Genetic code" isRequired={true} className={classes.fieldLabelReducedMargin} />
            <Text size="xs" color="dimmed" mb="xs">
              Select genetic code to translate DNA domains into AA domains
            </Text>
            <GeneticCodeDropdown
              geneticCode={translationParameters.geneticCode}
              onSelectGeneticCode={(geneticCode: CodonTranslationTable) => {
                setTranslationParameters({geneticCode});
              }}
            />
          </FormGroup>
        </Grid.Col>

        <Grid.Col span={6} {...getTestProps({component: 'FinalizePane', element: 'projectFolderField'})}>
          <FormGroup>
            <FieldLabel
              label="Project folder"
              isRequired={true}
              className={classes.fieldLabelReducedMargin}
            />
            <Text size="xs" color="dimmed" mb="xs">
              Set the project folder for proteins and components
            </Text>
            <ProjectFolderField
              folderId={projectFolderId}
              labelStyle={LABEL_STYLES.HIDDEN}
              onChange={(e: GenericEvent) => {
                setProjectFolder(e.target.value);
              }}
              isRequired={true}
            />
          </FormGroup>
        </Grid.Col>
      </Grid>

      {BenchlingConfig.ELN_ENABLE_ENTRY_SECTIONS && (
        <>
          <Divider my="lg" />

          <div {...getTestProps({component: 'FinalizePane', element: 'receiptSection'})}>
            <FieldLabel
              label="Add receipt to a Notebook entry"
              isRequired={false}
              className={classes.fieldLabelReducedMargin}
            />
            <Text size="xs" color="dimmed" mb="sm">
              Optionally send a receipt of protein creation to a notebook entry
            </Text>
            <Box w="50%">
              <FieldLabel label="Entry" />
              <EntrySearchInput
                onChange={(entries) => {
                  setSelectedDocumentId(entries[0]?.id || null);
                }}
                selectedEntries={selectedDocumentId ? [{id: selectedDocumentId}] : []}
                shouldAllowMultiple={false}
                autoFocus={false}
                onKeyDown={blurOnEnter}
                placeholder="Select entry"
                context={SearchContext.FOR_WRITE_ACTION}
              />
            </Box>
          </div>
        </>
      )}
    </div>
  );
}
